// src/App.js
import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";

import LoginPage from "./pages/LoginPage";
import ModuleSelectPage from "./pages/ModuleSelectPage";
import AdsAdminPage from "./pages/AdsAdminPage";
import AdsMonitorsPage from "./pages/AdsMonitorsPage";
import AdsIntelligencePage from "./pages/AdsIntelligencePage";
import BrandsHubPage from "./pages/BrandsHubPage";
import CostControlPage from "./pages/CostControlPage";
import AdminPage from "./pages/AdminPage";
import DocViewerPage from "./pages/DocViewerPage";
import AnalysisHistoryPage from "./pages/AnalysisHistoryPage";
import AnalysisReportPage from "./pages/AnalysisReportPage";
import CitationsPage from "./pages/CitationsPage";
import TemplatesPage from "./pages/TemplatesPage";
import PromptsAdminPage from "./pages/PromptsAdminPage";
import NewAnalysisPage from "./pages/NewAnalysisPage";
import QuestionLibraryPage from "./pages/QuestionLibraryPage";
import AnalysisRunDetailPage from "./pages/AnalysisRunDetailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";

import {
  getCurrentUser,
  fetchGlobalFeatureFlags,
  setCachedFeatureFlags,
  onAuthExpired,
  refreshSession,
} from "./services/api";

// --------------------------------------------------
// Feature gates
// Keep Ads deployed, but hidden/blocked until ready.
// --------------------------------------------------
const ENABLE_ADS_DISCOVERY = true;

function ProtectedRoute({ authed, children }) {
  if (!authed) return <Navigate to="/login" replace />;
  return children;
}

function AppInner() {
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  const authed = !!currentUser;

  // ---- Boot: recover session + preload flags once ----
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const me = await getCurrentUser();
        if (cancelled) return;

        setCurrentUser(me);

        try {
          const flags = await fetchGlobalFeatureFlags();
          setCachedFeatureFlags(flags);
        } catch {
          setCachedFeatureFlags({ copilotDisabled: false });
        }
      } catch {
        setCurrentUser(null);
        setCachedFeatureFlags({ copilotDisabled: false });
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Global auth-expired handler (emitted by api.js on any 401) ----
  useEffect(() => {
    const off = onAuthExpired(() => {
      setCurrentUser(null);
      navigate("/login", { replace: true });
    });

    return off;
  }, [navigate]);

  // ---- Activity-based keepalive ----
  useEffect(() => {
    if (!authed) return;

    const ACTIVE_WINDOW_MS = 6 * 60 * 1000;
    const REFRESH_MIN_GAP_MS = 2 * 60 * 1000;
    const TICK_MS = 30 * 1000;

    let lastActivityAt = Date.now();
    let lastRefreshAt = 0;
    let inFlight = false;

    const markActivity = () => {
      lastActivityAt = Date.now();
    };

    const events = [
      "click",
      "keydown",
      "mousemove",
      "scroll",
      "touchstart",
      "pointerdown",
    ];

    for (const ev of events) {
      window.addEventListener(ev, markActivity, { passive: true });
    }

    const tick = async () => {
      if (inFlight) return;

      const now = Date.now();
      const isActive = now - lastActivityAt <= ACTIVE_WINDOW_MS;
      const canRefresh = now - lastRefreshAt >= REFRESH_MIN_GAP_MS;

      if (!isActive || !canRefresh) return;

      inFlight = true;
      try {
        const me = await refreshSession();
        setCurrentUser(me);
        lastRefreshAt = Date.now();
      } catch {
        // api.js already handles 401 and broadcasts auth-expired
      } finally {
        inFlight = false;
      }
    };

    const intervalId = window.setInterval(tick, TICK_MS);

    return () => {
      window.clearInterval(intervalId);
      for (const ev of events) {
        window.removeEventListener(ev, markActivity);
      }
    };
  }, [authed]);

  if (checking) {
    return (
      <div style={{ padding: 24, fontFamily: "system-ui" }}>
        Checking session…
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={<Navigate to={authed ? "/modules" : "/login"} replace />}
      />

      <Route
        path="/login"
        element={
          authed ? (
            <Navigate to="/modules" replace />
          ) : (
            <LoginPage onLoggedIn={(me) => setCurrentUser(me)} />
          )
        }
      />

      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />

      <Route
        path="/modules"
        element={
          <ProtectedRoute authed={authed}>
            <ModuleSelectPage
              currentUser={currentUser}
              onLogout={() => setCurrentUser(null)}
            />
          </ProtectedRoute>
        }
      />

      {/* LLM169: legacy /llm surface retired - redirect to the Analysis Hub. */}
      <Route path="/llm" element={<Navigate to="/analyses" replace />} />

      <Route
        path="/ads"
        element={
          <ProtectedRoute authed={authed}>
            {ENABLE_ADS_DISCOVERY ? (
              <AdsAdminPage currentUser={currentUser} />
            ) : (
              <Navigate to="/modules" replace />
            )}
          </ProtectedRoute>
        }
      />

      <Route
        path="/ads/monitors"
        element={
          <ProtectedRoute authed={authed}>
            {ENABLE_ADS_DISCOVERY ? (
              <AdsMonitorsPage currentUser={currentUser} />
            ) : (
              <Navigate to="/modules" replace />
            )}
          </ProtectedRoute>
        }
      />

      <Route
        path="/ads/intelligence"
        element={
          <ProtectedRoute authed={authed}>
            {ENABLE_ADS_DISCOVERY ? (
              <AdsIntelligencePage currentUser={currentUser} />
            ) : (
              <Navigate to="/modules" replace />
            )}
          </ProtectedRoute>
        }
      />

      <Route
        path="/brands-hub"
        element={
          <ProtectedRoute authed={authed}>
            <BrandsHubPage
              currentUser={currentUser}
              onLogout={() => setCurrentUser(null)}
            />
          </ProtectedRoute>
        }
      />

      <Route
        path="/cost"
        element={
          <ProtectedRoute authed={authed}>
            {currentUser?.is_admin ? (
              <CostControlPage currentUser={currentUser} />
            ) : (
              <Navigate to="/modules" replace />
            )}
          </ProtectedRoute>
        }
      />

      <Route
        path="/analyses"
        element={
          <ProtectedRoute authed={authed}>
            <AnalysisHistoryPage currentUser={currentUser} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/analysis/:sessionId"
        element={
          <ProtectedRoute authed={authed}>
            <AnalysisReportPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/citations"
        element={
          <ProtectedRoute authed={authed}>
            <CitationsPage currentUser={currentUser} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/templates"
        element={
          <ProtectedRoute authed={authed}>
            <TemplatesPage currentUser={currentUser} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/prompts"
        element={
          <ProtectedRoute authed={authed}>
            <PromptsAdminPage currentUser={currentUser} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/brands/:brandId/new-analysis"
        element={
          <ProtectedRoute authed={authed}>
            <NewAnalysisPage currentUser={currentUser} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/brands/:brandId/questions"
        element={
          <ProtectedRoute authed={authed}>
            <QuestionLibraryPage currentUser={currentUser} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/analysis-runs/:runId"
        element={
          <ProtectedRoute authed={authed}>
            <AnalysisRunDetailPage currentUser={currentUser} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <ProtectedRoute authed={authed}>
            {currentUser?.is_admin ? (
              <AdminPage currentUser={currentUser} />
            ) : (
              <Navigate to="/modules" replace />
            )}
          </ProtectedRoute>
        }
      />

      <Route
        path="/view-doc/:filename"
        element={
          <ProtectedRoute authed={authed}>
            {currentUser?.is_admin ? (
              <DocViewerPage />
            ) : (
              <Navigate to="/modules" replace />
            )}
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}